import { html, css, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { msg } from '@lit/localize';
import { SsoElement } from './sso-base.js';
import { appIcon } from './app-icons.js';
import './sso-button.js';

/**
 * `<sso-app-tile>` — a launcher tile for one of the three Pinakes apps (SSO-MOCKUPS.md §8).
 *
 * Display-only card: icon, name, short description and an "Open" button. Clicking "Open" emits
 * an `open-app` CustomEvent<string> (the app key) — the launcher page opens `/go/{app}` in a new tab
 * (which 302s to the app with `?SSOCode=`).
 */
@customElement('sso-app-tile')
export class SsoAppTile extends SsoElement {
  static override styles = [
    SsoElement.styles,
    css`
      :host {
        display: block;
      }
      .tile {
        display: grid;
        gap: 0.5rem;
        justify-items: center;
        text-align: center;
        padding: var(--sso-space-lg);
        background: var(--sso-color-surface);
        border: 1px solid var(--sso-color-border);
        border-radius: var(--sso-radius);
        box-shadow: var(--sso-shadow-card);
        height: 100%;
      }
      .icon {
        width: 56px;
        height: 56px;
        border-radius: var(--sso-radius);
        background: color-mix(in srgb, var(--sso-color-soft-blue) 20%, var(--sso-color-surface));
        display: grid;
        place-items: center;
        font-weight: 700;
        /* Glyph in ink, name in brand blue — like the product icons on www.pinakes.be. */
        color: var(--sso-color-fg);
        overflow: hidden;
      }
      .icon img {
        width: 100%;
        height: 100%;
        object-fit: contain;
      }
      .icon svg {
        width: 30px;
        height: 30px;
      }
      .name {
        font-family: var(--sso-font-heading-family);
        font-weight: 600;
        color: var(--sso-color-brand);
      }
      .desc {
        color: var(--sso-color-muted);
        font-size: 0.9rem;
        margin: 0;
        flex: 1;
      }
      sso-button {
        margin-top: 0.5rem;
        width: auto;
        min-width: 8rem;
      }
    `,
  ];

  @property() app = '';
  @property() label = '';
  @property() description = '';
  @property() iconUrl = '';

  /** Focus the tile's "Open" button (used by the launcher for first-control focus). */
  override focus(): void {
    void this.updateComplete.then(() =>
      this.renderRoot.querySelector<HTMLElement>('sso-button')?.focus(),
    );
  }

  private open(): void {
    this.dispatchEvent(
      new CustomEvent<string>('open-app', { detail: this.app, bubbles: true, composed: true }),
    );
  }

  override render() {
    // Icon preference: explicit iconUrl > the app's built-in glyph > monogram fallback.
    // Monogram from the app KEY ('crm' → CRM, 'export' → EXP), not the label —
    // friendly labels all start with "Pinakes" and would collapse to "PIN".
    const short = (this.app || this.label).slice(0, 3).toUpperCase();
    return html`
      <div class="tile">
        <div class="icon" aria-hidden="true">
          ${this.iconUrl ? html`<img src=${this.iconUrl} alt="" />` : (appIcon(this.app) ?? short)}
        </div>
        <div class="name">${this.label}</div>
        ${this.description ? html`<p class="desc">${this.description}</p>` : nothing}
        <sso-button variant="primary" @click=${this.open}>${msg('Open')}</sso-button>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sso-app-tile': SsoAppTile;
  }
}
